import React, { ReactNode } from "react";

import {
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
} from "@mui/material";

import { IgButton } from "../IgButton";

interface IgMobileDialogProps extends Omit<DialogProps, "open" | "title"> {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;

  // Title/Subtitle
  title?: ReactNode;
  subtitle?: ReactNode;

  // Action Buttons
  yesButtonText?: string;
  noButtonText?: string | null;
  handleYesClick?: () => void;
  handleNoClick?: () => void;

  // Button States
  loading?: boolean;
  yesButtonDisabled?: boolean;
  yesButtonColor?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";

  // Layout Control
  hideActions?: boolean;
}

export default function IgMobileDialog({
  open,
  onClose,
  children,
  title,
  subtitle,
  yesButtonText,
  noButtonText = "Cancel",
  handleYesClick,
  handleNoClick,
  loading = false,
  yesButtonDisabled = false,
  yesButtonColor = "primary",
  hideActions = false,
  ...rest
}: IgMobileDialogProps) {
  const hasActions = !hideActions && (yesButtonText || noButtonText);

  const handleCancel = () => {
    if (handleNoClick) {
      handleNoClick();
    } else if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth {...rest}>
      {title && (
        <DialogTitle textAlign="center" fontWeight={600}>
          {title}
        </DialogTitle>
      )}
      <DialogContent>
        {subtitle && (
          <Stack sx={{ padding: 2, paddingBottom: 4, textAlign: "center" }}>
            {subtitle}
          </Stack>
        )}
        <Stack spacing={3}>
          {children}
          {hasActions && (
            <Stack
              direction="row"
              spacing={2}
              sx={{ pt: 2, textAlign: "center" }}
            >
              {noButtonText && (
                <IgButton
                  onClick={handleCancel}
                  disabled={loading}
                  color="secondary"
                  fullWidth
                >
                  {noButtonText}
                </IgButton>
              )}
              {yesButtonText && (
                <IgButton
                  onClick={handleYesClick}
                  disabled={yesButtonDisabled || loading}
                  loading={loading}
                  color={yesButtonColor}
                  fullWidth
                >
                  {yesButtonText}
                </IgButton>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
